import { useRouter } from "next/router";
import { useState } from "react";
import { useOrgTimezoneOrUtc } from "~/contexts/orgProvider";
import type { ActionBlockRow } from "~/shared/ai/action-block";
import {
  type ActionIntent,
  type ActionProposalArtifact,
  type ActionProposalItem,
  actionClarifyPicksIntent,
  fillActionLabel,
} from "~/shared/ai/assistant-action-artifact";
import type { AssistantEntrySurface } from "~/shared/ai/assistant-entry-surface";
import { ACTION_ACCENT } from "~/styles/palettes";
import { Anchor, Box, Button, Checkbox, Flex, Progress, Text } from "~/ui";
import { orgHref, ReceiptRowCorrect, receiptAttribution, receiptRowLink } from "./ActionReceipt";
import type { ActionExecutionState, ActionSelectionPatch } from "./action-artifact-state";
import { type ActionBlockBadge, ActionBlockFooter, ActionBlockFrame, ActionBlockHeader } from "./action-block/ActionBlockChrome";
import { ActionBlockGroups, ActionBlockRows } from "./action-block/ActionBlockRows";
import { ActionClarifyPick, ActionRowQuestion } from "./action-block/ActionRowQuestion";
import { BLOCK, chipStyle } from "./action-block/action-block-tokens";
import { ACTION_PREVIEW_ROWS } from "./action-preview";
import { ASSISTANT_ACTION_COPY } from "./assistant-action-copy";

type SubmitAction = (query: string, intent: ActionIntent) => Promise<void>;

const EMPTY_CHOICE_SET: ReadonlySet<string> = new Set();

const isReceiptStatus = (status: ActionProposalArtifact["status"]): boolean =>
  status === "APPLIED" || status === "PARTIALLY_APPLIED" || status === "STALE" || status === "FAILED";

/** Rows that cannot be saved for a reason of their own; rows left out are counted apart. */
const cannotSaveCount = (artifact: ActionProposalArtifact): number => artifact.counts.unsupported - (artifact.counts.leftOut ?? 0);

export function statusLabel(artifact: ActionProposalArtifact, execution?: ActionExecutionState, replacementVersion?: number): string {
  switch (artifact.status) {
    case "PENDING":
      return artifact.counts.needsInput > 0
        ? ASSISTANT_ACTION_COPY.decisionsNeeded(artifact.counts.needsInput)
        : cannotSaveCount(artifact) > 0
          ? ASSISTANT_ACTION_COPY.cannotSave(cannotSaveCount(artifact))
          : "Waiting to be saved";
    case "REPLACED":
      return replacementVersion ? ASSISTANT_ACTION_COPY.replacedBelow : "Updated";
    case "APPLYING":
      return artifact.resumable
        ? ASSISTANT_ACTION_COPY.savingStopped
        : `Saving · ${execution?.completed ?? 0} of ${execution?.total ?? artifact.counts.selected}`;
    case "APPLIED":
      return "Saved";
    case "PARTIALLY_APPLIED":
      return "Partly saved";
    case "DISMISSED":
    case "EXPIRED":
    case "STALE":
    case "FAILED":
      return ASSISTANT_ACTION_COPY.notSaved;
  }
}

/**
 * Header badges. Amber marks only an open decision that blocks saving; a receipt's title says it saved, so it wears no
 * badge, and a review ready to save needs none beside its Save button.
 */
export function headerBadges(
  artifact: ActionProposalArtifact,
  execution?: ActionExecutionState,
  replacementVersion?: number,
): ActionBlockBadge[] {
  switch (artifact.status) {
    case "PENDING":
      return [
        ...(artifact.counts.needsInput > 0
          ? [{ label: ASSISTANT_ACTION_COPY.decisionsNeeded(artifact.counts.needsInput), tone: "warn" as const }]
          : []),
        // A row that cannot save says why in its own words; the header adds no count of them.
      ];
    case "APPLIED":
    case "PARTIALLY_APPLIED":
      return [];
    case "APPLYING":
      return [{ label: statusLabel(artifact, execution, replacementVersion), tone: artifact.resumable ? "quiet" : "ready" }];
    default:
      return [{ label: statusLabel(artifact, execution, replacementVersion), tone: "quiet" }];
  }
}

export function cardTitle(artifact: ActionProposalArtifact): string {
  if (artifact.status === "APPLIED" || artifact.status === "PARTIALLY_APPLIED") {
    if (artifact.receiptTitle) {
      return artifact.receiptTitle;
    }
    const fallback = artifact.labels.receiptFallback;
    if (artifact.status === "APPLIED") {
      return artifact.receipt?.applied === 1 ? fallback.one : fillActionLabel(fallback.many, { count: artifact.receipt?.applied ?? 0 });
    }
    return fillActionLabel(fallback.partial, {
      applied: artifact.receipt?.applied ?? 0,
      total: artifact.receipt?.total ?? artifact.counts.selected,
    });
  }
  return artifact.title;
}

/** The header context parts the server chose for this review, on one line. */
export function summaryLine(artifact: ActionProposalArtifact): string {
  return artifact.context.join(" · ");
}

/** A saved review's counts on one line, naming only the counts that are not zero; a review waiting to be saved has none. */
export function reviewCountsLine(artifact: ActionProposalArtifact): string {
  if (artifact.status !== "APPLIED" && artifact.status !== "PARTIALLY_APPLIED") {
    return "";
  }
  return [`${artifact.receipt?.applied ?? 0} saved`, (artifact.counts.leftOut ?? 0) > 0 ? `${artifact.counts.leftOut} left out` : null]
    .filter((part): part is string => part !== null)
    .join(" · ");
}

/** The save rule a review states before its button: only a review whose rows save one by one says so. */
export function batchRule(artifact: ActionProposalArtifact): string | null {
  return artifact.policy.save === "together" ? null : ASSISTANT_ACTION_COPY.batchRuleIndependent;
}

/** Approve's label for this many rows, from the server's templates. */
export function approveControlLabel(artifact: Pick<ActionProposalArtifact, "labels">, selected: number): string {
  return selected === 1
    ? fillActionLabel(artifact.labels.approve.one, { count: selected })
    : fillActionLabel(artifact.labels.approve.many, { count: selected });
}

/** Rows can be checked and unchecked unless the review saves as one fixed set. */
export const selectionEnabledFor = (artifact: Pick<ActionProposalArtifact, "policy">): boolean => artifact.policy.select !== "fixed";

/** What happened to a review that did not save as asked, and what to do next. */
function lifecycleMessage(artifact: ActionProposalArtifact): string | null {
  if (artifact.status === "DISMISSED") {
    return ASSISTANT_ACTION_COPY.dismissed;
  }
  if (artifact.status === "EXPIRED") {
    return ASSISTANT_ACTION_COPY.expired;
  }
  if (artifact.status === "STALE") {
    return artifact.counts.selected === 1 ? artifact.labels.stale.one : artifact.labels.stale.many;
  }
  if (artifact.status === "APPLYING" && artifact.resumable) {
    return ASSISTANT_ACTION_COPY.interrupted(artifact.receipt?.applied ?? 0, artifact.counts.selected);
  }
  if (artifact.status === "PARTIALLY_APPLIED" && (artifact.receipt?.rejected ?? 0) > 0) {
    return fillActionLabel(artifact.labels.permissionLost, {
      applied: artifact.receipt?.applied ?? 0,
      total: artifact.receipt?.total ?? artifact.counts.selected,
    });
  }
  return artifact.status === "FAILED" ? ASSISTANT_ACTION_COPY.failed : null;
}

export const ActionProposalCard = ({
  artifact,
  execution,
  patch,
  surface,
  onStage,
  onSubmitAction,
  onOpenReview,
  onChange,
  onCorrect,
  onClarify,
  paneOpen = false,
  controlError,
  previewItems,
  replacementVersion,
}: {
  artifact: ActionProposalArtifact;
  execution?: ActionExecutionState;
  patch: ActionSelectionPatch;
  surface: AssistantEntrySurface;
  onStage: (patch: ActionSelectionPatch) => void;
  onSubmitAction: SubmitAction;
  onOpenReview: () => void;
  onChange?: () => void;
  onCorrect?: (item: ActionProposalItem) => void;
  onClarify?: (query: string) => Promise<void>;
  /** The review is open in the side pane, which is then the one place to act on it. */
  paneOpen?: boolean;
  /** Why the last press on this card did not go through, shown on the card's own line. */
  controlError?: string;
  /** The first rows of a review too large to carry inline, for display only. */
  previewItems?: ActionProposalItem[];
  replacementVersion?: number;
}) => {
  const router = useRouter();
  const orgTimezone = useOrgTimezoneOrUtc();
  const orgSlug = typeof router.query.orgSlug === "string" ? router.query.orgSlug : "";
  // Local staging for row questions: nothing durable exists until the answer is submitted as
  // a canonical turn, so component state is the honest home for the choices.
  const [chosenChoices, setChosenChoices] = useState<Record<string, ReadonlySet<string>>>({});
  const toggleChoice = (itemKey: string, choiceId: string) => {
    setChosenChoices(previous => {
      const next = new Set(previous[itemKey] ?? []);
      if (next.has(choiceId)) {
        next.delete(choiceId);
      } else {
        next.add(choiceId);
      }
      return { ...previous, [itemKey]: next };
    });
  };
  const items = artifact.inlineItems ?? [];
  // Rows to show: the review's own when it carries them, else the first page read for a preview. Selection and Save use
  // only the review's own rows, so a preview never narrows what saves.
  const shownSource = items.length > 0 ? items : (previewItems ?? []);
  const itemsByKey = new Map(shownSource.map(item => [item.itemKey, item]));
  const receiptMode = isReceiptStatus(artifact.status);
  const selectionEnabled = selectionEnabledFor(artifact);
  const selected = selectionEnabled
    ? artifact.counts.selected + patch.includeItemKeys.length - patch.excludeItemKeys.length
    : artifact.counts.selected;
  const binding = artifact.binding;
  const controllable = surface === "assistant_page" && binding !== undefined && artifact.status === "PENDING";
  // One place to act: while the side pane holds this review, the card keeps its rows and the person's checks, not its controls.
  const actingElsewhere = paneOpen && (artifact.status === "PENDING" || artifact.status === "APPLYING");
  const canControl = controllable && !paneOpen;
  const canRefine = artifact.refinement?.available !== false;
  const grouped = Boolean(artifact.groups) && !receiptMode;
  const intent = (kind: "approve" | "dismiss" | "resume"): ActionIntent | null =>
    binding
      ? kind === "approve"
        ? {
            kind,
            proposalId: artifact.proposalId,
            version: artifact.version,
            token: binding.token,
            selectedItemKeys: !selectionEnabled
              ? undefined
              : (patch.selectedItemKeys ??
                (artifact.inlineItems
                  ? items
                      .filter(
                        item =>
                          item.eligibility === "READY" &&
                          (patch.includeItemKeys.includes(item.itemKey) ||
                            (item.selected && !patch.excludeItemKeys.includes(item.itemKey))),
                      )
                      .map(item => item.itemKey)
                  : undefined)),
          }
        : { kind, proposalId: artifact.proposalId, version: artifact.version, token: binding.token }
      : null;
  const submit = (query: string, actionIntent: ActionIntent | null) =>
    actionIntent ? onSubmitAction(query, actionIntent) : Promise.resolve();
  const setIncluded = (itemKey: string, serverSelected: boolean, checked: boolean) => {
    const include = new Set(patch.includeItemKeys);
    const exclude = new Set(patch.excludeItemKeys);
    if (checked === serverSelected) {
      include.delete(itemKey);
      exclude.delete(itemKey);
    } else if (checked) {
      include.add(itemKey);
      exclude.delete(itemKey);
    } else {
      exclude.add(itemKey);
      include.delete(itemKey);
    }
    onStage({
      includeItemKeys: [...include],
      excludeItemKeys: [...exclude],
      selectedItemKeys: items
        .filter(item => item.eligibility === "READY" && (include.has(item.itemKey) || (item.selected && !exclude.has(item.itemKey))))
        .map(item => item.itemKey),
    });
  };
  const badges = headerBadges(artifact, execution, replacementVersion);
  const summary = summaryLine(artifact);
  const large = artifact.counts.candidates >= 7;

  // One live version: a replaced review collapses to one quiet line with no controls.
  if (artifact.status === "REPLACED" && replacementVersion !== undefined) {
    return (
      <Text
        role="status"
        data-action-review=""
        data-review-status={artifact.status}
        style={{ fontSize: BLOCK.meta, color: BLOCK.secondary, padding: "2px 0" }}
      >
        {ASSISTANT_ACTION_COPY.replacedBelow}
      </Text>
    );
  }

  if (surface === "ask_ai_popover") {
    const previewRows = shownSource
      .filter(item => item.row.state !== "left_out")
      .map(item => item.row)
      .slice(0, ACTION_PREVIEW_ROWS);
    const remaining = artifact.counts.candidates - previewRows.length;
    return (
      <ActionBlockFrame role="group" aria-label={artifact.title} data-action-review="" data-review-status={artifact.status}>
        <ActionBlockHeader title={cardTitle(artifact)} badges={badges} summary={summary} />
        {previewRows.length > 0 && (
          <Flex direction="column" style={{ padding: "4px 0" }}>
            {previewRows.map(row => {
              const first = row.fields.find(field => field.kind === "money");
              return (
                <Flex key={row.key} align="center" gap={8} style={{ padding: "6px 14px", fontSize: BLOCK.bodySize, minWidth: 0 }}>
                  <Text
                    style={{
                      flex: "1 1 auto",
                      minWidth: 0,
                      color: BLOCK.body,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.subject}
                    {row.secondary ? ` · ${row.secondary}` : ""}
                  </Text>
                  {first?.before && <Text style={{ color: BLOCK.secondary, whiteSpace: "nowrap" }}>{first.before}</Text>}
                  {first?.before && (
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke={BLOCK.faint} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  {first && <Text style={{ fontWeight: 600, color: BLOCK.ink, whiteSpace: "nowrap" }}>{first.after}</Text>}
                </Flex>
              );
            })}
            {remaining > 0 && (
              <Text style={{ padding: "4px 14px 6px", fontSize: BLOCK.meta, color: BLOCK.secondary }}>and {remaining} more</Text>
            )}
          </Flex>
        )}
        <ActionBlockFooter note={artifact.status === "PENDING" ? ASSISTANT_ACTION_COPY.opensFullScreen : undefined}>
          <Button size="xs" variant="light" onClick={onOpenReview}>
            {artifact.status === "PENDING" ? ASSISTANT_ACTION_COPY.openReview : ASSISTANT_ACTION_COPY.openReviewSaved}
          </Button>
        </ActionBlockFooter>
      </ActionBlockFrame>
    );
  }

  const renderLead =
    (items.length > 1 || artifact.display.singleRowCheckbox) && selectionEnabled && !receiptMode
      ? (row: ActionBlockRow) => {
          const item = itemsByKey.get(row.key);
          if (!item) {
            return null;
          }
          const checked = patch.includeItemKeys.includes(item.itemKey)
            ? true
            : patch.excludeItemKeys.includes(item.itemKey)
              ? false
              : item.selected;
          return (
            <Checkbox
              aria-label={`Include ${row.subject}${row.secondary ? ` · ${row.secondary}` : ""}`}
              checked={checked}
              disabled={item.eligibility !== "READY" || artifact.status !== "PENDING" || actingElsewhere}
              onChange={event => setIncluded(item.itemKey, item.selected, event.currentTarget.checked)}
            />
          );
        }
      : undefined;

  const renderQuestion = (row: ActionBlockRow) => {
    const item = itemsByKey.get(row.key);
    if (!item?.question) {
      return null;
    }
    const chosenSet = chosenChoices[item.itemKey] ?? EMPTY_CHOICE_SET;
    return (
      <ActionRowQuestion
        itemKey={item.itemKey}
        subject={item.row.subject}
        question={item.question}
        canControl={canControl}
        chosenSet={chosenSet}
        onToggleChoice={choiceId => toggleChoice(item.itemKey, choiceId)}
        onClarify={onClarify}
        onStructuredClarify={
          binding
            ? (picks: readonly ActionClarifyPick[]) => {
                const query = ASSISTANT_ACTION_COPY.chosenRowsTurn(
                  picks.map(pick => {
                    const subject = itemsByKey.get(pick.itemKey)?.row.subject ?? pick.label;
                    return { subject, label: pick.label, reply: pick.reply };
                  }),
                );
                return onSubmitAction(
                  query,
                  actionClarifyPicksIntent({ proposalId: artifact.proposalId, version: artifact.version, token: binding.token }, picks),
                );
              }
            : undefined
        }
      />
    );
  };

  const applied = artifact.status === "APPLIED" || artifact.status === "PARTIALLY_APPLIED";
  // A saved row keeps one quiet Correct, and its identity routes to its record when the header cannot land on several.
  const renderActions = applied
    ? (row: ActionBlockRow) => (
        <ReceiptRowCorrect
          item={itemsByKey.get(row.key)}
          correctLabel={artifact.labels.correct}
          onCorrect={canRefine ? onCorrect : undefined}
        />
      )
    : undefined;
  const subjectLink = applied ? (row: ActionBlockRow) => receiptRowLink(itemsByKey.get(row.key), orgSlug) : undefined;
  const headerAside = applied
    ? artifact.headerLinks.map(link => (
        <Anchor key={link.label} href={orgHref(orgSlug, link.href)} style={{ fontSize: BLOCK.bodySize, fontWeight: 500 }}>
          {link.label}
        </Anchor>
      ))
    : artifact.status === "PENDING" && artifact.counts.candidates > 1
      ? batchRule(artifact)
      : null;
  const lifecycle = controlError ?? lifecycleMessage(artifact);
  const footerNote = applied
    ? receiptAttribution(artifact, orgTimezone)
    : actingElsewhere
      ? ASSISTANT_ACTION_COPY.openInPane
      : artifact.status === "PENDING"
        ? artifact.labels.footer
        : null;
  // An open question shown on the card is the next step; the footer does not repeat it or offer another way to change.
  const questionOnCard = !large && artifact.status === "PENDING" && artifact.counts.needsInput > 0;
  const showFooter = canControl || (artifact.status === "APPLYING" && artifact.resumable && binding) || footerNote;
  // Ungrouped reviews fold left-out rows into one trailing line and, past what the card shows, preview the first rows.
  const previewing = large && !grouped;
  const presentItems = shownSource.filter(item => item.row.state !== "left_out");
  const foldItems = shownSource.filter(item => item.row.state === "left_out");
  // Lines of the request that never became rows (a receipt's unanswered or unread sheet lines) fold in with their reason.
  const foldLines = grouped ? [] : (artifact.extraRows ?? []).flatMap(row => (row.group === "left_out" && "text" in row ? [row] : []));
  const visibleItems = previewing ? presentItems.slice(0, ACTION_PREVIEW_ROWS) : presentItems;
  const foldRowCount = previewing ? Math.max(artifact.counts.leftOut ?? 0, foldItems.length) : foldItems.length;
  const foldCount = foldRowCount + foldLines.length;
  // "and <n> more" counts the rows the preview hides; folded sheet lines are not rows.
  const moreCount = previewing ? Math.max(0, artifact.counts.candidates - foldRowCount - visibleItems.length) : 0;
  const rowMode = receiptMode ? "receipt" : "review";

  // One place to act: while the side pane holds this review or its receipt, the card is only its header and where to find it.
  if (paneOpen) {
    return (
      <ActionBlockFrame
        role="group"
        aria-labelledby={`action-title-${artifact.proposalId}`}
        data-action-review=""
        data-review-status={artifact.status}
      >
        <ActionBlockHeader titleId={`action-title-${artifact.proposalId}`} title={cardTitle(artifact)} badges={badges} summary={summary} />
        <ActionBlockFooter note={ASSISTANT_ACTION_COPY.openInPane} />
      </ActionBlockFrame>
    );
  }

  return (
    <ActionBlockFrame
      role="group"
      aria-labelledby={`action-title-${artifact.proposalId}`}
      data-action-review=""
      data-review-status={artifact.status}
    >
      <ActionBlockHeader
        titleId={`action-title-${artifact.proposalId}`}
        title={cardTitle(artifact)}
        badges={badges}
        summary={summary}
        aside={headerAside}
      />
      {large && grouped ? (
        <Flex align="center" justify="space-between" gap="sm" wrap="wrap" style={{ padding: "12px 18px" }}>
          <Text style={{ fontSize: BLOCK.bodySize, color: BLOCK.body }}>{artifact.subject}</Text>
          {(artifact.status === "PENDING" || artifact.status === "APPLYING") && !actingElsewhere && (
            <Button size="xs" variant="light" onClick={onOpenReview}>
              {ASSISTANT_ACTION_COPY.seeAll}
            </Button>
          )}
        </Flex>
      ) : grouped && artifact.groups ? (
        <ActionBlockGroups
          groups={artifact.groups}
          extraRows={artifact.extraRows ?? []}
          items={items}
          pageItems={items}
          canControl={canControl}
          onClarify={onClarify}
          renderItems={groupItems => (
            <ActionBlockRows
              rows={groupItems.map(item => item.row)}
              mode="review"
              layout="wide"
              renderLead={renderLead}
              renderQuestion={renderQuestion}
            />
          )}
        />
      ) : (
        <>
          <ActionBlockRows
            rows={visibleItems.map(item => item.row)}
            mode={rowMode}
            layout="wide"
            renderLead={previewing ? undefined : renderLead}
            renderQuestion={previewing ? undefined : renderQuestion}
            renderActions={renderActions}
            subjectLink={subjectLink}
          />
          {previewing && (
            <Flex
              align="center"
              justify="space-between"
              gap="sm"
              wrap="wrap"
              style={{ padding: "10px 18px", borderTop: `1px solid ${BLOCK.divider}` }}
            >
              <Text style={{ fontSize: BLOCK.bodySize, color: BLOCK.secondary }}>
                {moreCount > 0 ? ASSISTANT_ACTION_COPY.andMore(moreCount) : artifact.subject}
              </Text>
              {!actingElsewhere && (
                <Button size="xs" variant="light" onClick={onOpenReview}>
                  {ASSISTANT_ACTION_COPY.seeAll}
                </Button>
              )}
            </Flex>
          )}
          {foldCount > 0 && (
            <details style={{ padding: "10px 18px", borderTop: `1px solid ${BLOCK.divider}` }}>
              <summary style={{ cursor: "pointer", fontSize: BLOCK.bodySize, color: BLOCK.secondary }}>
                {ASSISTANT_ACTION_COPY.leftOut(foldCount)}
              </summary>
              {foldItems.length > 0 && <ActionBlockRows rows={foldItems.map(item => item.row)} mode={rowMode} layout="wide" />}
              {foldLines.map(line => (
                <Text key={line.key} style={{ padding: "6px 0 0", fontSize: BLOCK.meta, color: BLOCK.secondary }}>
                  {line.text}
                </Text>
              ))}
            </details>
          )}
        </>
      )}
      {(lifecycle || (artifact.status === "APPLYING" && !artifact.resumable)) && (
        <Box style={{ padding: "8px 18px 0" }}>
          {lifecycle && (
            <Text style={{ fontSize: BLOCK.bodySize, color: artifact.status === "FAILED" ? BLOCK.body : BLOCK.secondary }}>
              {lifecycle}
            </Text>
          )}
          {artifact.status === "APPLYING" && !artifact.resumable && (
            <Progress
              mt="xs"
              mb="sm"
              value={artifact.counts.selected > 1 ? ((execution?.completed ?? 0) / artifact.counts.selected) * 100 : undefined}
              animate
              role="progressbar"
              aria-valuenow={execution?.completed ?? 0}
              aria-valuemax={artifact.counts.selected}
            />
          )}
        </Box>
      )}
      {!canRefine && (onChange || onCorrect) && (
        <Text style={{ padding: "8px 18px 0", fontSize: BLOCK.meta, color: BLOCK.secondary }}>
          {ASSISTANT_ACTION_COPY.inactiveConversation}
        </Text>
      )}
      {showFooter && (
        <ActionBlockFooter note={footerNote}>
          {canControl && selected > 0 && artifact.policy.approvable && !artifact.display.approveInFullReviewOnly && (
            <Button
              size="sm"
              data-action-control="approve"
              style={{ backgroundColor: ACTION_ACCENT }}
              onClick={() =>
                void submit(
                  selected === 1 ? ASSISTANT_ACTION_COPY.approveOneTurn : ASSISTANT_ACTION_COPY.approveManyTurn,
                  intent("approve"),
                )
              }
            >
              {approveControlLabel(artifact, selected)}
            </Button>
          )}
          {canControl && !artifact.policy.approvable && artifact.policy.reason && !questionOnCard && (
            <Text style={{ fontSize: BLOCK.meta, color: BLOCK.secondary }}>{artifact.policy.reason}</Text>
          )}
          {canControl && onChange && canRefine && !questionOnCard && (
            <Button size="sm" variant="subtle" data-action-control="change" onClick={onChange}>
              {artifact.labels.change}
            </Button>
          )}
          {canControl && (
            <Button
              size="sm"
              variant="subtle"
              color="gray"
              style={{ color: BLOCK.secondary }}
              data-action-control="dismiss"
              onClick={() => void submit(ASSISTANT_ACTION_COPY.dismissTurn, intent("dismiss"))}
            >
              {ASSISTANT_ACTION_COPY.dismiss}
            </Button>
          )}
          {artifact.status === "APPLYING" && artifact.resumable && binding && !actingElsewhere && (
            <>
              <Button size="sm" variant="light" color="yellow" onClick={() => void submit(ASSISTANT_ACTION_COPY.resume, intent("resume"))}>
                {ASSISTANT_ACTION_COPY.resume}
              </Button>
              {(artifact.receipt?.applied ?? 0) === 0 && (
                <Button
                  size="sm"
                  variant="subtle"
                  color="gray"
                  style={{ color: BLOCK.secondary }}
                  onClick={() => void submit(ASSISTANT_ACTION_COPY.dismissTurn, intent("dismiss"))}
                >
                  {ASSISTANT_ACTION_COPY.dismiss}
                </Button>
              )}
            </>
          )}
          {artifact.status === "PENDING" && artifact.counts.candidates > 1 && !controllable && (
            <Text style={{ fontSize: BLOCK.meta, color: BLOCK.secondary }}>{artifact.labels.consequence}</Text>
          )}
        </ActionBlockFooter>
      )}
      {!showFooter && shownSource.length === 0 && (
        <Box style={{ padding: 12 }}>
          <span style={chipStyle("neutral")}>{statusLabel(artifact, execution, replacementVersion)}</span>
        </Box>
      )}
    </ActionBlockFrame>
  );
};
